/**
 * AP-12 — Single-Window-Mode als Split-Pane.
 *
 * Gegenstück zu `CanMonitorApp::EmbedView()`, `UnembedCurrentView()`,
 * `OnMainSplitterDrag()` und dem Split-Zweig in `LayoutControls()`
 * (CanMonitorApp.cpp:1742…1758).
 *
 * Verhalten wie im Original:
 * - Ohne eingebettete View bekommt die Frametabelle die **ganze** Fläche;
 *   Splitter und untere Fläche existieren dann gar nicht.
 * - Es ist immer nur **eine** View eingebettet ("only one embedded view at a
 *   time, per spec"). Eine zweite verdrängt die erste.
 * - Der Teiler ist auf 0.15…0.85 begrenzt (`OnMainSplitterDrag`) und wird in
 *   den Einstellungen gehalten (`mainSplitterRatio_`, config.txt).
 * - Wird der Single-Window-Mode abgeschaltet, löst sich die View sofort
 *   (`if (!singleWindowMode_) UnembedCurrentView();`).
 *
 * Die eingebettete View behält ihre eigene Titelzeile — im Original bleibt es
 * dasselbe `HWND`, nur ohne Popup-Rahmen. Sie bekommt zusätzlich eine
 * "Undock"-Schaltfläche, weil es in der PWA kein Fenstermenü gibt, über das
 * man sie sonst wieder lösen könnte.
 */

import type { AppStore } from './store';
import { createSplitter } from '../ui/controls';
import { cancelPendingEmbed, embedNextShow, type FloatingPanel } from '../views/filters/FloatingPanel';

/** `OnMainSplitterDrag`: `std::max(0.15, std::min(0.85, ratio))`. */
const MIN_RATIO = 0.15;
const MAX_RATIO = 0.85;

export interface AppShell {
  /** Der Split-Bereich, gehört zwischen Werkzeugleiste und Statuszeile. */
  element: HTMLElement;
}

function clampRatio(ratio: number): number {
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));
}

export function createAppShell(store: AppStore, tableElement: HTMLElement): AppShell {
  const split = document.createElement('div');
  split.className = 'cm-split';

  let docked: FloatingPanel | null = null;

  const topPane = document.createElement('div');
  topPane.className = 'cm-pane cm-pane--table';
  topPane.append(tableElement);

  const dock = document.createElement('div');
  dock.className = 'cm-dock';

  const undockButton = document.createElement('button');
  undockButton.type = 'button';
  undockButton.className = 'cm-dock__undock';
  undockButton.textContent = 'Undock';
  undockButton.title = 'Als eigenes Fenster l\u00f6sen';

  const splitter = createSplitter(split, {
    orientation: 'horizontal',
    minRatio: MIN_RATIO,
    onRatioChanged: (ratio) => applyRatio(ratio, true),
  });

  split.append(topPane, splitter, dock);
  applyRatio(store.settings.splitterRatio, false);
  setDocked(null);

  function applyRatio(ratio: number, persist: boolean): void {
    const clamped = clampRatio(ratio);
    split.style.setProperty('--split-ratio', String(clamped));
    if (persist) store.setSplitterRatio(clamped);
  }

  /** `EmbedView()` / `UnembedCurrentView()` in einem. */
  function setDocked(panel: FloatingPanel | null): void {
    if (docked !== null && docked !== panel) docked.release();
    docked = panel;

    const hasView = panel !== null;
    splitter.hidden = !hasView;
    dock.hidden = !hasView;
    split.classList.toggle('cm-split--docked', hasView);

    if (panel !== null) {
      panel.embed(dock);
      // Die Undock-Schaltfläche gehört in die Titelzeile der View, damit sie
      // wie eine Fenstersteuerung wirkt und nicht wie Inhalt.
      const header = panel.element.querySelector('.cm-panel-window__header');
      header?.insertBefore(undockButton, header.lastElementChild);
    } else if (undockButton.parentElement !== null) {
      undockButton.remove();
    }
  }

  undockButton.addEventListener('click', () => {
    const panel = docked;
    setDocked(null);
    // Gelöst heißt sichtbar weitermachen, nicht schließen — das Original
    // versteckt beim Lösen nur, weil es dort keinen Weg zurück gibt.
    panel?.show();
  });

  // Reihenfolge zählt: dieser Zuhörer muss VOR mountViewsA/B/C/D registriert
  // werden, damit `embedNextShow()` gesetzt ist, wenn die View `show()` ruft.
  store.on('view:opened', (event) => {
    if (!event.embedded) {
      cancelPendingEmbed();
      return;
    }
    embedNextShow((panel) => {
      setDocked(panel);
      // Schließt der Benutzer die angedockte View über ihr eigenes ×, muss
      // die Tabelle die ganze Fläche zurückbekommen.
      panel.onHide(() => {
        if (docked === panel) setDocked(null);
      });
    });
    // Alle View-Zuhörer laufen synchron während `emit`. Greift keiner den
    // Wunsch auf (unbekannte `ViewKind`), darf er nicht liegenbleiben und das
    // nächste, völlig unbeteiligte `show()` andocken.
    queueMicrotask(cancelPendingEmbed);
  });

  store.on('settings:changed', (event) => {
    if (event.changed.includes('singleWindowMode') && !event.settings.singleWindowMode) {
      setDocked(null);
    }
  });

  return { element: split };
}
